export type StatusBadgeContext = 'orders' | 'invoices' | 'quotes';
export type StatusBadgeVariant = 'success' | 'info' | 'warning' | 'danger' | 'muted' | 'default';

const ALLOWED_STATUS_BADGE_VARIANTS = new Set<StatusBadgeVariant>([
    'success',
    'info',
    'warning',
    'danger',
    'muted',
    'default',
]);

const STATUS_BADGE_ALIASES: Record<string, string> = {
    approved: 'pending',
    'quote-completed': 'completed',
    'partially-paid': 'partial',
};

const DEFAULT_STATUS_BADGE_MAPPINGS: Record<string, StatusBadgeVariant> = {
    completed: 'success',
    processing: 'info',
    pending: 'warning',
    paid: 'success',
    closed: 'success',
    open: 'warning',
    unpaid: 'warning',
    overdue: 'warning',
    balanced: 'info',
    partial: 'info',
    'on-hold': 'muted',
    cancelled: 'danger',
    refunded: 'danger',
    failed: 'danger',
    void: 'danger',
    'bad-debt': 'danger',
    'awaiting-payment': 'info',
    converted: 'muted',
    rejected: 'danger',
    draft: 'muted',
};

const normalizeStatusBadgeStatus = (status?: string): string => (
    String(status || '')
        .toLowerCase()
        .replace(/[_\s]+/g, '-')
        .trim()
);

const getStatusBadgeMappingKey = (status?: string): string => {
    const normalizedStatus = normalizeStatusBadgeStatus(status);
    return STATUS_BADGE_ALIASES[normalizedStatus] || normalizedStatus;
};

const getLegacyStatusBadgeMappingKey = (context: StatusBadgeContext, status?: string): string => (
    `${context}:${normalizeStatusBadgeStatus(status)}`
);

const getConfiguredStatusBadgeMappings = (): Record<string, string> => {
    const configuredMappings = (window as any)?.wekaApiSettings?.theme?.status_badge_mappings;
    return configuredMappings && typeof configuredMappings === 'object' ? configuredMappings as Record<string, string> : {};
};

export const getStatusBadgeVariant = (context: StatusBadgeContext, status?: string): StatusBadgeVariant => {
    const mappingKey = getStatusBadgeMappingKey(status);
    const configuredMappings = getConfiguredStatusBadgeMappings();
    const configuredVariant = configuredMappings[mappingKey] || configuredMappings[getLegacyStatusBadgeMappingKey(context, status)];

    if (configuredVariant && ALLOWED_STATUS_BADGE_VARIANTS.has(configuredVariant as StatusBadgeVariant)) {
        return configuredVariant as StatusBadgeVariant;
    }

    return DEFAULT_STATUS_BADGE_MAPPINGS[mappingKey] || 'default';
};

export const getStatusBadgeModifierClass = (context: StatusBadgeContext, status?: string): string => (
    `is-${getStatusBadgeVariant(context, status)}`
);

export const getStatusBadgeClassName = (context: StatusBadgeContext, status?: string): string => (
    `weka-status-badge ${getStatusBadgeModifierClass(context, status)}`
);
